import React, { useState, useContext, useEffect, useReducer } from "react";
import {
  Button,
  Menu,
  MenuItem,
  Box,
  makeStyles,
  Badge,
  Divider,
  Typography,
} from "@material-ui/core";
import {
  Dashboard as DashboardIcon,
  WhatsApp as WhatsAppIcon,
  Forum as ForumIcon,
  Schedule as ScheduleIcon,
  Announcement as CampaignIcon,
  LocalOffer as LocalOfferIcon,
  ExpandMore as ExpandMoreIcon,
  Contacts as ContactsIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Call as CallIcon,
  Speed as BoltIcon,
  ViewModule as ViewKanbanIcon,
  HelpOutline as HelpOutlineIcon,
  Code as CodeIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  AccountTree as AccountTreeIcon,
  AttachFile as AttachFileIcon,
  SyncAlt as SyncAltIcon,
  PhoneAndroid as PhoneAndroidIcon,
  LocalAtm as LocalAtmIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  Apps as GridViewIcon,
  Cake as CakeIcon,
  CloudQueue as CloudQueueIcon,
  AllInclusive as AllInclusiveIcon,
  DeviceHub as DeviceHubIcon,
  PlaylistAdd as PlaylistAddIcon,
  ContactMail as ContactMailIcon,
  Tune as TuneIcon,
  TrendingUp as AutoGraphIcon,
  Chat as SmartToyIcon,
  Description as IntegrationInstructionsIcon,
} from "@material-ui/icons";
import { useHistory, useLocation } from "react-router-dom";
import { AuthContext } from "../context/Auth/AuthContext";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { Can } from "../components/Can";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import useHelps from "../hooks/useHelps";
import useSettings from "../hooks/useSettings";
import { i18n } from "../translate/i18n";
import useCompanySettings from "../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
  menuSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  navButton: {
    margin: theme.spacing(0, 0.25),
    textTransform: "none",
    fontWeight: 500,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    minWidth: "auto",
    fontSize: "0.75rem",
    height: "32px",
    whiteSpace: "nowrap",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.1)",
    },
    "&.active": {
      backgroundColor: "rgba(255,255,255,0.2)",
      color: "#ffffff",
      "&:hover": {
        backgroundColor: "rgba(255,255,255,0.3)",
      },
    },
  },
  menuItem: {
    padding: theme.spacing(0.75, 1.5),
    minWidth: 140,
    display: "flex",
    alignItems: "center",
    fontSize: "0.875rem",
    "& svg": {
      marginRight: theme.spacing(1),
      fontSize: "1rem",
    },
  },
  expandIcon: {
    fontSize: "0.9rem !important",
    marginLeft: theme.spacing(0.25),
  },
  divider: {
    height: "24px",
    margin: theme.spacing(0, 1),
  },
  badge: {
    "& .MuiBadge-badge": {
      backgroundColor: "#ef4444",
      color: "#fff",
      fontSize: "0.6rem",
      fontWeight: 700,
      minWidth: "16px",
      height: "16px",
    }
  },
  sectionTitle: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    padding: theme.spacing(0.5, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(0.5),
  },
}));

const HorizontalMenu = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user, socket } = useContext(AuthContext);
  const { whatsApps } = useContext(WhatsAppsContext);

  // Estados para os menus dropdown
  const [managementAnchor, setManagementAnchor] = useState(null);
  const [conversationsAnchor, setConversationsAnchor] = useState(null);
  const [schedulingAnchor, setSchedulingAnchor] = useState(null);
  const [crmAnchor, setCrmAnchor] = useState(null);
  const [campaignsAnchor, setCampaignsAnchor] = useState(null);
  const [automationsAnchor, setAutomationsAnchor] = useState(null);
  const [adminAnchor, setAdminAnchor] = useState(null);

  // Estados das funcionalidades
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showWavoipCall, setShowWavoipCall] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [showWallets, setShowWallets] = useState(false);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [hasHelps, setHasHelps] = useState(false);
  const [invisible, setInvisible] = useState(true);

  // Hook calls
  const { getPlanCompany } = usePlans();
  const { list } = useHelps();
  const { get: getSetting } = useSettings();

  // Route helper functions
  const isManagementActive = location.pathname === "/" || location.pathname.startsWith("/reports") || location.pathname.startsWith("/moments");
  const isConversationsActive = location.pathname.startsWith("/tickets") || location.pathname.startsWith("/chats");
  const isSchedulingActive = location.pathname.startsWith("/schedules");
  const isCrmActive = location.pathname.startsWith("/contacts") || location.pathname.startsWith("/tags") || location.pathname.startsWith("/quick-messages") || location.pathname.startsWith("/schedules") || location.pathname.startsWith("/calcom-integration");
  const isCampaignsActive = location.pathname.startsWith("/campaigns") || location.pathname.startsWith("/contact-lists");
  const isAutomationsActive = location.pathname.startsWith("/phrase-lists") || location.pathname.startsWith("/flowbuilders") || location.pathname.startsWith("/plugins/floup");
  const isAdminActive = location.pathname.startsWith("/users") || location.pathname.startsWith("/settings") || location.pathname.startsWith("/companies");

  const [chats, dispatch] = useReducer((state, action) => {
    if (action.type === "LOAD_CHATS") {
      const chats = action.payload;
      const newChats = [];
      if (isArray(chats)) {
        chats.forEach((chat) => {
          const chatIndex = state.findIndex((u) => u.id === chat.id);
          if (chatIndex !== -1) {
            state[chatIndex] = chat;
          } else {
            newChats.push(chat);
          }
        });
      }
      return [...state, ...newChats];
    }
    return state;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const planConfigs = await getPlanCompany();
      setShowCampaigns(planConfigs.plan.useCampaigns);
      setShowKanban(planConfigs.plan.useKanban);
      setShowOpenAi(planConfigs.plan.useOpenAi);
      setShowIntegrations(planConfigs.plan.useIntegrations);
      setShowSchedules(planConfigs.plan.useSchedules);
      setShowInternalChat(planConfigs.plan.useInternalChat);
      setShowExternalApi(planConfigs.plan.useExternalApi);
      setShowWavoipCall(planConfigs.plan.wavoip);
    };

    if (user?.companyId) {
      fetchData();
    }
  }, [user?.companyId, getPlanCompany]);

  useEffect(() => {
    async function checkHelps() {
      const helps = await list();
      setHasHelps(helps.length > 0);
    }
    checkHelps();
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const setting = await getSetting({ "column": "DirectTicketsToWallets" });
        setShowWallets(setting.DirectTicketsToWallets);
      } catch (err) {
        toastError(err);
      }
    }
    fetchSettings();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const handleNavigation = (path) => {
    history.push(path);
    // Fechar todos os menus
    setManagementAnchor(null);
    setConversationsAnchor(null);
    setSchedulingAnchor(null);
    setCrmAnchor(null);
    setCampaignsAnchor(null);
    setAutomationsAnchor(null);
    setAdminAnchor(null);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", width: "100%", overflow: "auto" }}>
      {/* Seção Principal - Esquerda */}
      <Box className={classes.menuSection}>
        {/* Gerenciamento */}
        <Can
          role={
            (user.profile === "user" && user.showDashboard === "enabled") ||
              user.allowRealTime === "enabled"
              ? "admin"
              : user.profile
          }
          perform={"drawer-admin-items:view"}
          yes={() => (
            <>
              <Button
                className={`${classes.navButton} ${isManagementActive ? "active" : ""}`}
                startIcon={<DashboardIcon />}
                endIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                onClick={(e) => setManagementAnchor(e.currentTarget)}
              >
                Gerenciamento
              </Button>
              <Menu
                anchorEl={managementAnchor}
                open={Boolean(managementAnchor)}
                onClose={() => setManagementAnchor(null)}
              >
                <Typography className={classes.sectionTitle}>Dashboard</Typography>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/")}>
                  <BarChartIcon />
                  Dashboard
                </MenuItem>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/reports")}>
                  <AssessmentIcon />
                  {i18n.t("mainDrawer.listItems.reports")}
                </MenuItem>
                <Can
                  role={user.profile === "user" && user.allowRealTime === "enabled" ? "admin" : user.profile}
                  perform={"drawer-admin-items:view"}
                  yes={() => (
                    <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/moments")}>
                      <GridViewIcon />
                      {i18n.t("mainDrawer.listItems.chatsTempoReal")}
                    </MenuItem>
                  )}
                />
                {user.profile === "admin" && (
                  <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/plugins/floup/dashboard")}>
                    <ViewKanbanIcon />
                    Painel FollowUP
                  </MenuItem>
                )}
                {user.profile === "admin" && showWallets && (
                  <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/wallets")}>
                    <AccountBalanceWalletIcon />
                    {i18n.t("mainDrawer.listItems.wallets")}
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        />

        {/* Conversas */}
        <Button
          className={`${classes.navButton} ${isConversationsActive ? "active" : ""}`}
          startIcon={<ForumIcon />}
          endIcon={<ExpandMoreIcon className={classes.expandIcon} />}
          onClick={(e) => setConversationsAnchor(e.currentTarget)}
        >
          Conversas
        </Button>
        <Menu
          anchorEl={conversationsAnchor}
          open={Boolean(conversationsAnchor)}
          onClose={() => setConversationsAnchor(null)}
        >
          <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/tickets")}>
            <WhatsAppIcon />
            Atendimento
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/chats")}>
            <Badge color="secondary" variant="dot" invisible={invisible} className={classes.badge}>
              <ForumIcon />
            </Badge>
            Chat Interno
          </MenuItem>
        </Menu>

        {/* Agendamentos */}
        {showSchedules && (
          <>
            <Button
              className={`${classes.navButton} ${isSchedulingActive ? "active" : ""}`}
              startIcon={<ScheduleIcon />}
              endIcon={<ExpandMoreIcon className={classes.expandIcon} />}
              onClick={(e) => setSchedulingAnchor(e.currentTarget)}
            >
              Agendamentos
            </Button>
            <Menu
              anchorEl={schedulingAnchor}
              open={Boolean(schedulingAnchor)}
              onClose={() => setSchedulingAnchor(null)}
            >
              <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/schedules")}>
                <WhatsAppIcon />
                WhatsApp
              </MenuItem>
            </Menu>
          </>
        )}

        {/* CRM */}
        <Button
          className={`${classes.navButton} ${isCrmActive ? "active" : ""}`}
          startIcon={<ContactsIcon />}
          endIcon={<ExpandMoreIcon className={classes.expandIcon} />}
          onClick={(e) => setCrmAnchor(e.currentTarget)}
        >
          CRM
        </Button>
        <Menu
          anchorEl={crmAnchor}
          open={Boolean(crmAnchor)}
          onClose={() => setCrmAnchor(null)}
        >
          <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/schedules")}>
            <ScheduleIcon />
            Agenda
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/tags")}>
            <LocalOfferIcon />
            {i18n.t("mainDrawer.listItems.tags")}
          </MenuItem>
          {user.showContacts === "enabled" && (
            <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/contacts")}>
              <ContactsIcon />
              {i18n.t("mainDrawer.listItems.contacts")}
            </MenuItem>
          )}
          <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/quick-messages")}>
            <BoltIcon />
            {i18n.t("mainDrawer.listItems.quickMessages")}
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/kanban")}>
            <ViewKanbanIcon />
            Kanban
          </MenuItem>
        </Menu>

        <Divider orientation="vertical" className={classes.divider} />

        {/* Links Diretos */}
        {showWavoipCall && (
          <Button
            className={classes.navButton}
            startIcon={<CallIcon />}
            onClick={() => handleNavigation("/call-historicals")}
          >
            Histórico Chamadas
          </Button>
        )}

        {hasHelps && (
          <Button
            className={classes.navButton}
            startIcon={<HelpOutlineIcon />}
            onClick={() => handleNavigation("/helps")}
          >
            Ajuda
          </Button>
        )}
      </Box>

      {/* Seção Secundária - Direita */}
      <Box className={classes.menuSection}>
        {/* Campanhas */}
        <>
          <Button
            className={`${classes.navButton} ${isCampaignsActive ? "active" : ""}`}
            startIcon={<CampaignIcon />}
            endIcon={<ExpandMoreIcon className={classes.expandIcon} />}
            onClick={(e) => setCampaignsAnchor(e.currentTarget)}
          >
            Campanhas
          </Button>
          <Menu
            anchorEl={campaignsAnchor}
            open={Boolean(campaignsAnchor)}
            onClose={() => setCampaignsAnchor(null)}
          >
            <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/campaigns")}>
              <PlaylistAddIcon />
              {i18n.t("campaigns.subMenus.list")}
            </MenuItem>
            <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/contact-lists")}>
              <ContactMailIcon />
              {i18n.t("campaigns.subMenus.listContacts")}
            </MenuItem>
            <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/campaigns-config")}>
              <TuneIcon />
              {i18n.t("campaigns.subMenus.settings")}
            </MenuItem>
          </Menu>
        </>

        {/* Automações */}
        <Button
          className={`${classes.navButton} ${isAutomationsActive ? "active" : ""}`}
          startIcon={<CodeIcon />}
          endIcon={<ExpandMoreIcon className={classes.expandIcon} />}
          onClick={(e) => setAutomationsAnchor(e.currentTarget)}
        >
          Automações
        </Button>
        <Menu
          anchorEl={automationsAnchor}
          open={Boolean(automationsAnchor)}
          onClose={() => setAutomationsAnchor(null)}
        >
          <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/phrase-lists")}>
            <AutoGraphIcon />
            Fluxo de Campanha
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/flowbuilders")}>
            <SmartToyIcon />
            Fluxo de Conversa
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/plugins/floup")}>
            <IntegrationInstructionsIcon />
            Follow UP
          </MenuItem>
        </Menu>

        <Divider orientation="vertical" className={classes.divider} />

        {/* Administração */}
        <Can
          role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
          perform="dashboard:view"
          yes={() => (
            <>
              <Button
                className={`${classes.navButton} ${isAdminActive ? "active" : ""}`}
                startIcon={<SettingsIcon />}
                endIcon={<ExpandMoreIcon className={classes.expandIcon} />}
                onClick={(e) => setAdminAnchor(e.currentTarget)}
              >
                Administração
              </Button>
              <Menu
                anchorEl={adminAnchor}
                open={Boolean(adminAnchor)}
                onClose={() => setAdminAnchor(null)}
              >
                <Typography className={classes.sectionTitle}>Sistema</Typography>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/users")}>
                  <PeopleIcon />
                  {i18n.t("mainDrawer.listItems.users")}
                </MenuItem>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/queues")}>
                  <AccountTreeIcon />
                  {i18n.t("mainDrawer.listItems.queues")}
                </MenuItem>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/settings")}>
                  <SettingsIcon />
                  {i18n.t("mainDrawer.listItems.settings")}
                </MenuItem>

                {/* Integrações e API como itens diretos */}
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/queue-integration")}>
                  <DeviceHubIcon />
                  Integrações
                </MenuItem>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/messages-api")}>
                  <CodeIcon />
                  API
                </MenuItem>

                <Divider />
                <Typography className={classes.sectionTitle}>Integrações</Typography>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/prompts")}>
                  <AllInclusiveIcon />
                  {i18n.t("mainDrawer.listItems.prompts")}
                </MenuItem>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/queue-integration")}>
                  <DeviceHubIcon />
                  {i18n.t("mainDrawer.listItems.queueIntegration")}
                </MenuItem>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/messages-api")}>
                  <CodeIcon />
                  {i18n.t("mainDrawer.listItems.messagesAPI")}
                </MenuItem>

                <Divider />
                <Typography className={classes.sectionTitle}>Conexões</Typography>
                <Can
                  role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
                  perform={"drawer-admin-items:view"}
                  yes={() => (
                    <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/connections")}>
                      <Badge color="error" variant="dot" invisible={!connectionWarning} className={classes.badge}>
                        <SyncAltIcon />
                      </Badge>
                      {i18n.t("mainDrawer.listItems.connections")}
                    </MenuItem>
                  )}
                />

                <Divider />
                <Typography className={classes.sectionTitle}>Outros</Typography>
                {user.super && (
                  <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/announcements")}>
                    <CampaignIcon />
                    {i18n.t("mainDrawer.listItems.annoucements")}
                  </MenuItem>
                )}
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/birthday-settings")}>
                  <CakeIcon />
                  {i18n.t("mainDrawer.listItems.birthdaySettings")}
                </MenuItem>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/files")}>
                  <AttachFileIcon />
                  {i18n.t("mainDrawer.listItems.files")}
                </MenuItem>
                <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/financeiro")}>
                  <LocalAtmIcon />
                  {i18n.t("mainDrawer.listItems.financeiro")}
                </MenuItem>
                {user.super && (
                  <>
                    <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/allConnections")}>
                      <PhoneAndroidIcon />
                      {i18n.t("mainDrawer.listItems.allConnections")}
                    </MenuItem>
                    <MenuItem className={classes.menuItem} onClick={() => handleNavigation("/companies")}>
                      <BusinessIcon />
                      {i18n.t("mainDrawer.listItems.companies")}
                    </MenuItem>
                  </>
                )}
              </Menu>
            </>
          )}
        />
      </Box>
    </div>
  );
};

export default HorizontalMenu;